import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { updateMe } from "../../api/clientPortalPublic";
import { useClientPortalAuth } from "../../context/ClientPortalAuthContext";
import { formatApiError } from "../../utils/formatApiError";

const emptyForm = {
  full_name: "",
  email: "",
  phone: "",
  is_diabetic: false,
  has_health_concerns: false,
  medical_concern_details: "",
  service_waiver_accepted: false,
  service_waiver_accepted_at: null,
  address1: "",
  address2: "",
  city: "",
  state_province: "",
  postal_code: "",
};

const SERVICE_WAIVER_PLACEHOLDER = `Placeholder service waiver text for salon/legal review.

This section is intended to become salon-configurable in a future update. Until then, clients should review this notice before saving profile information:

- Services can involve close contact, tools, products, and chemicals.
- Clients should disclose medical concerns, allergies, sensitivities, or other health information that could affect safe service delivery.
- The salon may adjust, decline, or stop a service when health or safety concerns are identified.
- Sharing accurate information helps the salon provide service more safely.`;

const normalizeValue = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed ? trimmed : null;
};

export default function PortalProfile() {
  const { salonId } = useOutletContext();
  const navigate = useNavigate();
  const { token, client, clientMe, setClient, clientLogout } =
    useClientPortalAuth();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [waiverError, setWaiverError] = useState("");

  useEffect(() => {
    let active = true;
    const loadProfile = async () => {
      if (!token) {
        navigate(`/portal/salon/${salonId}/login`, { replace: true });
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await clientMe();
        if (!active) return;
        if (data) {
          setForm({
            full_name: data.full_name || "",
            email: data.email || "",
            phone: data.phone || "",
            is_diabetic: !!data.is_diabetic,
            has_health_concerns: !!data.has_health_concerns,
            medical_concern_details: data.medical_concern_details || "",
            service_waiver_accepted: !!data.service_waiver_accepted,
            service_waiver_accepted_at: data.service_waiver_accepted_at || null,
            address1: data.address1 || "",
            address2: data.address2 || "",
            city: data.city || "",
            state_province: data.state_province || "",
            postal_code: data.postal_code || "",
          });
        }
      } catch (err) {
        if (!active) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          clientLogout();
          navigate(`/portal/salon/${salonId}/login`, { replace: true });
          return;
        }
        setError("Unable to load your profile.");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadProfile();
    return () => {
      active = false;
    };
  }, [token, clientMe, salonId, navigate, clientLogout]);

  useEffect(() => {
    if (!client) return;
    setForm({
      full_name: client.full_name || "",
      email: client.email || "",
      phone: client.phone || "",
      is_diabetic: !!client.is_diabetic,
      has_health_concerns: !!client.has_health_concerns,
      medical_concern_details: client.medical_concern_details || "",
      service_waiver_accepted: !!client.service_waiver_accepted,
      service_waiver_accepted_at: client.service_waiver_accepted_at || null,
      address1: client.address1 || "",
      address2: client.address2 || "",
      city: client.city || "",
      state_province: client.state_province || "",
      postal_code: client.postal_code || "",
    });
  }, [client]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggle = (field) => (event) => {
    const checked = !!event.target.checked;
    setForm((prev) => ({ ...prev, [field]: checked }));
    if (field === "service_waiver_accepted") {
      setWaiverError("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving(true);
    setError("");
    setSuccess("");
    setWaiverError("");

    const fullName = form.full_name.trim();
    if (!fullName) {
      setError("Full name is required.");
      setSaving(false);
      return;
    }
    if (!form.service_waiver_accepted) {
      const message = "You must read and agree to the service waiver before saving.";
      setError(message);
      setWaiverError(message);
      setSaving(false);
      return;
    }

    try {
      const payload = {
        full_name: fullName,
        email: normalizeValue(form.email),
        phone: normalizeValue(form.phone),
        is_diabetic: !!form.is_diabetic,
        has_health_concerns: !!form.has_health_concerns,
        medical_concern_details: form.has_health_concerns
          ? normalizeValue(form.medical_concern_details)
          : null,
        service_waiver_accepted: !!form.service_waiver_accepted,
        address1: normalizeValue(form.address1),
        address2: normalizeValue(form.address2),
        city: normalizeValue(form.city),
        state_province: normalizeValue(form.state_province),
        postal_code: normalizeValue(form.postal_code),
      };
      const updated = await updateMe(payload, token);
      setClient(updated);
      setSuccess("Profile updated successfully.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600">
        Loading profile...
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
              Your profile
            </h2>
            <p className="mt-1 text-sm md:text-base text-slate-600">
              Keep your contact details up to date for faster booking.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/portal/salon/${salonId}/book`}
              className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Book now
            </Link>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">
                Full name
              </label>
              <input
                type="text"
                value={form.full_name}
                onChange={handleChange("full_name")}
                className="h-11 w-full rounded-xl border border-slate-300 px-4"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                className="h-11 w-full rounded-xl border border-slate-300 px-4"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={handleChange("phone")}
                className="h-11 w-full rounded-xl border border-slate-300 px-4"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-semibold text-slate-600">
                Address line 1
              </label>
              <input
                type="text"
                value={form.address1}
                onChange={handleChange("address1")}
                className="h-11 w-full rounded-xl border border-slate-300 px-4"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-semibold text-slate-600">
                Address line 2
              </label>
              <input
                type="text"
                value={form.address2}
                onChange={handleChange("address2")}
                className="h-11 w-full rounded-xl border border-slate-300 px-4"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">City</label>
              <input
                type="text"
                value={form.city}
                onChange={handleChange("city")}
                className="h-11 w-full rounded-xl border border-slate-300 px-4"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">
                State / Province
              </label>
              <input
                type="text"
                value={form.state_province}
                onChange={handleChange("state_province")}
                className="h-11 w-full rounded-xl border border-slate-300 px-4"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">
                Postal code
              </label>
              <input
                type="text"
                value={form.postal_code}
                onChange={handleChange("postal_code")}
                className="h-11 w-full rounded-xl border border-slate-300 px-4"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-5">
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Health &amp; Safety
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Share any health information the salon should review before service.
              </p>
            </div>
            <div className="mt-4 space-y-4">
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={!!form.is_diabetic}
                  onChange={handleToggle("is_diabetic")}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900"
                />
                <span>I am diabetic</span>
              </label>

              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={!!form.has_health_concerns}
                  onChange={handleToggle("has_health_concerns")}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900"
                />
                <span>
                  I have medical concerns the salon should know about before service
                </span>
              </label>

              {form.has_health_concerns && (
                <div className="space-y-2">
                  <label
                    htmlFor="portal-medical-concern-details"
                    className="text-sm font-semibold text-slate-600"
                  >
                    Medical concern details
                  </label>
                  <textarea
                    id="portal-medical-concern-details"
                    value={form.medical_concern_details}
                    onChange={handleChange("medical_concern_details")}
                    rows={5}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3"
                    placeholder="Please share any medical concerns, allergies, sensitivities, or other health information the salon should know to provide service safely."
                  />
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-5">
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Service Waiver
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Review and accept the service waiver before saving your profile.
              </p>
            </div>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-700 whitespace-pre-line">
              {SERVICE_WAIVER_PLACEHOLDER}
            </div>
            <label className="mt-4 flex items-start gap-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!form.service_waiver_accepted}
                onChange={handleToggle("service_waiver_accepted")}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900"
              />
              <span>I have read and agree to the service waiver</span>
            </label>
            {waiverError && (
              <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {waiverError}
              </div>
            )}
            {form.service_waiver_accepted_at && (
              <p className="mt-3 text-xs text-slate-500">
                Waiver accepted on {new Date(form.service_waiver_accepted_at).toLocaleString()}.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
            <Link
              to={`/portal/salon/${salonId}`}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
            >
              Back to portal
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
